import { useCallback, useState } from 'react';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import type {
  ITenantRepository,
  TenantMembershipDTO,
  TenantMembershipsResult,
} from '@/domain/repositories/ITenantRepository';

function apiErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string } } }).response?.data;
    if (data?.message) return data.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export const useMyTenantMemberships = () => {
  const [email, setEmail] = useState('');
  const [memberships, setMemberships] = useState<TenantMembershipDTO[]>([]);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [credentialsLocked, setCredentialsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const repo = () => container.get<ITenantRepository>(TYPES.ITenantRepository);

  const applyMemberships = (result: TenantMembershipsResult) => {
    setEmail(result.email);
    setMemberships(result.memberships);
    setHasCredentials(Boolean(result.hasCredentials));
    setCredentialsLocked(Boolean(result.credentialsLocked));
  };

  const loadMemberships = useCallback(async (nextEmail?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await repo().getMyMemberships(nextEmail);
      applyMemberships(result);
      return result;
    } catch (err) {
      const next = new Error(apiErrorMessage(err, 'No se pudieron cargar tus clínicas'));
      setError(next);
      throw next;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addMembership = useCallback(async (tenantId: string | number, nextEmail?: string) => {
    setBusyId('add');
    setError(null);
    try {
      const created = await repo().addMyMembership(tenantId, nextEmail);
      setMemberships((prev) => {
        const without = prev.filter((item) => item.id !== created.id);
        return [created, ...without];
      });
      return created;
    } catch (err) {
      const next = new Error(apiErrorMessage(err, 'No se pudo agregar la clínica'));
      setError(next);
      throw next;
    } finally {
      setBusyId(null);
    }
  }, []);

  const updateMembership = useCallback(
    async (id: string, patch: { tenantId?: string | number; activo?: boolean }, nextEmail?: string) => {
      setBusyId(id);
      setError(null);
      try {
        const updated = await repo().updateMyMembership(id, patch, nextEmail);
        setMemberships((prev) => prev.map((item) => (item.id === id ? updated : item)));
        return updated;
      } catch (err) {
        const next = new Error(apiErrorMessage(err, 'No se pudo actualizar el registro'));
        setError(next);
        throw next;
      } finally {
        setBusyId(null);
      }
    },
    []
  );

  const deleteMembership = useCallback(async (id: string, nextEmail?: string) => {
    setBusyId(id);
    setError(null);
    try {
      await repo().deleteMyMembership(id, nextEmail);
      setMemberships((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      const next = new Error(apiErrorMessage(err, 'No se pudo borrar el registro'));
      setError(next);
      throw next;
    } finally {
      setBusyId(null);
    }
  }, []);

  const changeEmail = useCallback(async (fromEmail: string, toEmail: string) => {
    setBusyId('email');
    setError(null);
    try {
      const result = await repo().changeMembershipsEmail(fromEmail, toEmail);
      applyMemberships(result);
      return result;
    } catch (err) {
      const next = new Error(apiErrorMessage(err, 'No se pudo cambiar el email'));
      setError(next);
      throw next;
    } finally {
      setBusyId(null);
    }
  }, []);

  const updateLoginPassword = useCallback(async (nextEmail: string, password: string, sendEmail?: boolean) => {
    setBusyId('password');
    setError(null);
    try {
      const result = await repo().updateClinicLoginPassword(nextEmail, password, sendEmail);
      setHasCredentials(Boolean(result.hasCredentials));
      setCredentialsLocked(Boolean(result.credentialsLocked));
      return result;
    } catch (err) {
      const next = new Error(apiErrorMessage(err, 'No se pudo actualizar la contraseña'));
      setError(next);
      throw next;
    } finally {
      setBusyId(null);
    }
  }, []);

  return {
    email,
    memberships,
    hasCredentials,
    credentialsLocked,
    isLoading,
    busyId,
    error,
    loadMemberships,
    addMembership,
    updateMembership,
    deleteMembership,
    changeEmail,
    updateLoginPassword,
  };
};
